import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected, PayloadAction } from '@reduxjs/toolkit'
import * as service from './services'
import { RootState } from '../../../redux/store'
import { CustomerTraining, UserTrainingMetadata } from '../../../types'
import { Task } from '../tasks/types'
import * as shipmentService from '../shipments/shipmentService'
import { AutoShipment, UpcomingShipment } from '../shipments/types'
import * as taskService from '../tasks/service'
import { DahsboardTraining } from './types'

const SLICE_PREFIX = 'practiceDashboard'
export type LoadingState = 'not-asked' | 'loading' | 'done' | 'error'

interface PracticeDashboardState {
  loadingState: LoadingState
  customerNumber: string | null
  tasks: Task[] | null
  customerTrainings: CustomerTraining[] | null
  userTrainingMetadata: UserTrainingMetadata[] | null
  upcomingShipments: Record<number, UpcomingShipment[]> | null
  autoShipments: AutoShipment[]
}

const initialState: PracticeDashboardState = {
  loadingState: 'not-asked',
  customerNumber: null,
  tasks: null,
  customerTrainings: null,
  userTrainingMetadata: null,
  upcomingShipments: null,
  autoShipments: [],
}

export const getPracticeTasks = createAsyncThunk(
  `${SLICE_PREFIX}/getCustomerTasks`,
  async (customerNumber: string) => {
    const includeLogs = true
    const tasks = await taskService.getTasks(customerNumber, includeLogs)
    return { tasks }
  }
)

export const getCustomerTraining = createAsyncThunk(
  `${SLICE_PREFIX}/getCustomerTraining`,
  async (customerNumber: string): Promise<DahsboardTraining> => {
    const data = await service.getCustomerTraining(customerNumber)
    return data
  }
)

export const getUpcomingShipments = createAsyncThunk(
  `${SLICE_PREFIX}/getUpcomingShipments`,
  async (customerNumber: string): Promise<Record<number, UpcomingShipment[]>> => {
    const data = await shipmentService.getUpcomingShipments(customerNumber)
    return data
  }
)

export const getAutoShipments = createAsyncThunk(
  `${SLICE_PREFIX}/getAutoShipments`,
  async (customerNumber: string): Promise<AutoShipment[]> => {
    const data = await shipmentService.getAutoShipments(customerNumber);
    return data;
  }
)

export const getDashboardData = createAsyncThunk(
  `${SLICE_PREFIX}/getDashboardData`,
  async (customerNumber: string, { dispatch }) => {
    await Promise.all([
      dispatch(getCustomerTraining(customerNumber)),
      dispatch(getPracticeTasks(customerNumber)),
      dispatch(getUpcomingShipments(customerNumber)),
      dispatch(getAutoShipments(customerNumber))
    ])

    return customerNumber
  }
)

const practiceDashboardSlice = createSlice({
  name: 'practiceDashboard',
  initialState,
  reducers: {
    clearDashboardCustomer: () => {
      return initialState
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCustomerTraining.fulfilled, (state, action: PayloadAction<DahsboardTraining>) => {
      state.customerTrainings = action.payload.customer_training || []
      state.userTrainingMetadata = action.payload.user_training_metadata || []
    })

    builder.addCase(getPracticeTasks.fulfilled, (state, { payload }) => {
      state.tasks = payload.tasks
    })

    builder.addCase(getUpcomingShipments.fulfilled, (state, action) => {
      state.upcomingShipments = action.payload
    })

    builder.addCase(getAutoShipments.fulfilled, (state, { payload }) => {
      state.autoShipments = payload;
    })

    builder.addMatcher(isPending(getDashboardData), (state) => {
      state.loadingState = 'loading'
    })

    builder.addMatcher(isRejected(getDashboardData), (state) => {
      state.loadingState = 'error'
    })

    builder.addMatcher(isFulfilled(getDashboardData), (state, { payload }) => {
      state.customerNumber = payload
      state.loadingState = 'done'
    })
  }
})

export const { clearDashboardCustomer } = practiceDashboardSlice.actions

export const selectCustomerNumber = (state: RootState) => state.practiceDashboard.customerNumber
export const selectTasks = (state: RootState) => state.practiceDashboard.tasks
export const selectCustomerTrainings = (state: RootState) => state.practiceDashboard.customerTrainings
export const selectUserTrainingMetadata = (state: RootState) => state.practiceDashboard.userTrainingMetadata
export const selectUpcomingShipments = (state: RootState) => state.practiceDashboard.upcomingShipments
export const selectLoadingState = (state: RootState) => state.practiceDashboard.loadingState
export const selectAutoShipments = (state: RootState) => state.practiceDashboard.autoShipments;

export default practiceDashboardSlice.reducer
